import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { z } from 'zod'
import { Permissions } from '../auth/rbac'
import { useAccess } from '../auth/useAccess'
import { useDebouncedValue } from '../hooks/useDebouncedValue'
import { api } from '../lib/api'
import { formatDateTimeMinute } from '../lib/date'
import ListContainer from '../ui/ListContainer'
import { useToast } from '../ui/ToastProvider'

// 템플릿 스키마 정의
const templateSchema = z.object({
  title: z.string()
    .min(1, '제목은 필수입니다')
    .max(100, '제목은 100자 이하로 입력해주세요')
    .regex(/^[가-힣a-zA-Z0-9\s\-_()]+$/, '제목에는 한글, 영문, 숫자, 공백, 하이픈, 언더스코어, 괄호만 사용 가능합니다'),
  content: z.string()
    .min(10, '내용은 최소 10자 이상 입력해주세요')
    .max(5000, '내용은 5000자 이하로 입력해주세요')
    .refine((content) => {
      const forbiddenTags = ['<script', '<iframe', '<object', '<embed', '<link', '<meta']
      return !forbiddenTags.some(tag => content.toLowerCase().includes(tag))
    }, '허용되지 않는 HTML 태그가 포함되어 있습니다'),
  variables: z.array(z.string()).optional(),
  category: z.enum(['GENERAL', 'URGENT', 'MAINTENANCE', 'ANNOUNCEMENT']).default('GENERAL'),
  isActive: z.boolean().default(true)
})

// 공지 템플릿 관리 페이지
// - 템플릿 목록/검색/미리보기/생성/수정/삭제 + 테스트 발송 요청 관리
// - Zod로 강력한 유효성 검증 수행
export default function NoticeTemplatesPage() {
  const { show } = useToast()
  const { guard, allow } = useAccess()
  const [q, setQ] = useState('')
  const dq = useDebouncedValue(q, 300)
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const [title, setTitle] = useState('')
  const [content, setContent] = useState('')
  const [category, setCategory] = useState('GENERAL')
  const [isActive, setIsActive] = useState(true)
  const [preview, setPreview] = useState(null)
  const [editingId, setEditingId] = useState(null)
  const [validationErrors, setValidationErrors] = useState({})
  const [saving, setSaving] = useState(false)

  const reload = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const params = {}
      if (dq) params.q = dq
      const res = await api.get('/notice-templates', { params })
      const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
      setItems(Array.isArray(payload) ? payload : payload?.items || [])
    } catch (e) {
      setError('템플릿을 불러오지 못했습니다')
    } finally {
      setLoading(false)
    }
  }, [dq])

  useEffect(() => {
    let ignore = false
      ; (async () => {
        if (ignore) return
        await reload()
      })()
    return () => { ignore = true }
  }, [dq, reload])

  const validateForm = useCallback(() => {
    try {
      templateSchema.parse({ title, content, category, isActive })
      setValidationErrors({})
      return true
    } catch (error) {
      if (error instanceof z.ZodError) {
        const errors = {}
        error.errors.forEach(err => { errors[err.path[0]] = err.message })
        setValidationErrors(errors)
      }
      return false
    }
  }, [title, content, category, isActive])

  useEffect(() => {
    if (title || content) {
      validateForm()
    }
  }, [title, content, category, validateForm])

  const onSave = async (e) => {
    e.preventDefault()
    if (!allow.perm([Permissions.TEMPLATE_MANAGE])) {
      show('권한이 없습니다', 'error')
      return
    }
    if (!validateForm()) {
      show('입력 정보를 확인해주세요', 'error')
      return
    }
    setSaving(true)
    try {
      const payload = { title, content, category, isActive }
      if (editingId) {
        await api.patch(`/notice-templates/${editingId}`, payload)
        show('템플릿 수정 완료', 'success')
      } else {
        await api.post('/notice-templates', payload)
        show('템플릿 생성 완료', 'success')
      }
      setTitle('')
      setContent('')
      setCategory('GENERAL')
      setIsActive(true)
      setEditingId(null)
      setValidationErrors({})
      await reload()
    } catch (err) {
      show('템플릿 저장 실패', 'error')
    } finally {
      setSaving(false)
    }
  }

  const filtered = useMemo(() => items, [items])

  return (
    <section>
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">공지 템플릿</h1>
      </div>
      <div className="mt-4 flex items-center gap-2">
        <input className="w-full md:w-80 rounded border px-3 py-2 text-sm" placeholder="검색: 제목" value={q} onChange={(e) => setQ(e.target.value)} />
      </div>

      <div className="grid md:grid-cols-2 gap-6 mt-6">
        <div className="rounded border">
          <h2 className="font-semibold p-4 pb-0">템플릿 목록</h2>
          <ListContainer
            loading={loading}
            error={error}
            onRetry={() => reload()}
            header={null}
            table={(
              <div className="mt-3 overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-slate-50 text-slate-600">
                    <tr>
                      <th className="px-3 py-2 text-left">제목</th>
                      <th className="px-3 py-2 text-left">최근 수정</th>
                      <th className="px-3 py-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map(t => (
                      <tr key={t.id} className="border-t">
                        <td className="px-3 py-2">{t.title}</td>
                        <td className="px-3 py-2">{formatDateTimeMinute(t.updatedAt)}</td>
                        <td className="px-3 py-2 text-right space-x-2">
                          <button {...guard.hideIfNoPerm([Permissions.TEMPLATE_MANAGE])} className="text-brand-navy hover:underline" onClick={() => setPreview(t)}>미리보기</button>
                          <button {...guard.hideIfNoPerm([Permissions.TEMPLATE_MANAGE])} className="text-slate-700 hover:underline" onClick={async () => { try { await api.post(`/notice-templates/${t.id}/test`); show('테스트 발송 요청', 'success') } catch { show('테스트 발송 실패', 'error') } }}>테스트</button>
                          <button {...guard.hideIfNoPerm([Permissions.TEMPLATE_MANAGE])} className="text-slate-700 hover:underline" onClick={() => { setEditingId(t.id); setTitle(t.title || ''); setContent(t.content || '') }}>수정</button>
                          <button {...guard.hideIfNoPerm([Permissions.TEMPLATE_MANAGE])} className="text-red-700 hover:underline" onClick={async () => { try { await api.delete(`/notice-templates/${t.id}`); show('삭제 완료', 'success'); await reload() } catch { show('삭제 실패', 'error') } }}>삭제</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            footer={null}
          />
        </div>
        <form onSubmit={onSave} className="rounded border p-4 space-y-3">
          <h2 className="font-semibold">{editingId ? '템플릿 수정' : '새 템플릿'}</h2>

          <div>
            <input className={`w-full rounded border px-3 py-2 ${validationErrors.title ? 'border-red-500' : ''}`} placeholder="제목" value={title} onChange={(e) => setTitle(e.target.value)} required />
            {validationErrors.title && (<div className="text-red-500 text-xs mt-1">{validationErrors.title}</div>)}
          </div>

          <div>
            <textarea className={`w-full rounded border px-3 py-2 ${validationErrors.content ? 'border-red-500' : ''}`} rows={8} placeholder="내용" value={content} onChange={(e) => setContent(e.target.value)} />
            {validationErrors.content && (<div className="text-red-500 text-xs mt-1">{validationErrors.content}</div>)}
            <div className="text-xs text-slate-500 mt-1">{content.length}/5000자</div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm text-slate-600 mb-1">카테고리</label>
              <select className="w-full rounded border px-3 py-2" value={category} onChange={(e) => setCategory(e.target.value)}>
                <option value="GENERAL">일반</option>
                <option value="URGENT">긴급</option>
                <option value="MAINTENANCE">점검</option>
                <option value="ANNOUNCEMENT">공지</option>
              </select>
            </div>
            <div className="flex items-center">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} className="rounded" />
                활성 상태
              </label>
            </div>
          </div>

          <div className="flex gap-2">
            <button {...guard.disableIfNoPerm([Permissions.TEMPLATE_MANAGE])} type="submit" className="rounded bg-brand-navy text-white px-3 py-2 disabled:opacity-50" disabled={saving || Object.keys(validationErrors).length > 0}>
              {saving ? '저장 중...' : (editingId ? '수정' : '생성')}
            </button>
            <button className="rounded border px-3 py-2" onClick={() => setPreview({ title, content, category })} type="button">미리보기</button>
            {editingId && (
              <button {...guard.disableIfNoPerm([Permissions.TEMPLATE_MANAGE])} type="button" className="rounded border px-3 py-2" onClick={() => { setEditingId(null); setTitle(''); setContent(''); setCategory('GENERAL'); setIsActive(true); setValidationErrors({}) }}>취소</button>
            )}
          </div>
        </form>
      </div>

      {preview && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center p-4" onClick={() => setPreview(null)}>
          <div className="w-full max-w-lg rounded bg-white p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold">미리보기: {preview.title}</h3>
            <div className="mt-3 whitespace-pre-wrap text-sm text-slate-700">{preview.content}</div>
            <div className="mt-4 text-right">
              <button className="rounded border px-3 py-2 text-sm" onClick={() => setPreview(null)}>닫기</button>
            </div>
          </div>
        </div>
      )}
    </section>
  )
}


